import { useEffect, useRef, useState } from 'react';

export interface NavigationLinkItem {
  linkText: string;
  linkURL: string;
}

export interface NavigationLinkProps {
  isActive?: boolean;
  baseLinkURL: NavigationLinkItem;
  dropdownLinks?: NavigationLinkItem[];
  mobile?: boolean;
}

function NavigationLink(props: NavigationLinkProps) {
  const { mobile = false } = props;

  let headerTextClassNames = 'text-md font-medium decoration-solid';
  if (props.isActive) {
    headerTextClassNames += ' underline';
  }

  // reuse header decoration/typography for dropdown items so styles match
  let dropdownItemClassNames =
    headerTextClassNames +
    ' block px-4 py-2 text-center duration-200 whitespace-nowrap';

  let hasDropdown: boolean = false;
  if (props.dropdownLinks) {
    hasDropdown = props.dropdownLinks.length > 0 ? true : false;
  }
  let dropdownList: NavigationLinkItem[] = [];
  if (hasDropdown && props.dropdownLinks) {
    dropdownList = props.dropdownLinks;
  }

  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  function clearCloseTimeout() {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }

  function scheduleClose(delay = 150) {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, delay);
  }
  const submenuId = `submenu-${props.baseLinkURL.linkText.replace(/\s+/g, '-').toLowerCase()}`;

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, []);

  if (mobile) {
    return (
      <div className="w-full" onKeyDown={handleKeyDown}>
        {/* Row: link text + optional chevron toggle */}
        <div className="flex items-center justify-between w-full">
          <a
            href={props.baseLinkURL.linkURL}
            className={headerTextClassNames + ' flex-1 py-1'}
          >
            {props.baseLinkURL.linkText}
          </a>

          {hasDropdown && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-controls={submenuId}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${
                props.baseLinkURL.linkText
              } submenu`}
              className="ml-2 p-1 rounded text-light-text hover:bg-light-border
                         focus:outline-none focus:ring-2 focus:ring-light-main/40
                         transition-colors duration-150"
            >
              <img
                src="/icons/chevron-down.svg"
                alt=""
                aria-hidden="true"
                className={`w-3 h-3 transition-transform duration-200 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </button>
          )}
        </div>

        {/* Accordion body — max-height transition gives a smooth expand/collapse */}
        {hasDropdown && (
          <div
            id={submenuId}
            role="menu"
            className={`flex flex-col pl-3 ml-1 border-l-2 border-light-border
                        overflow-hidden transition-all duration-200 ease-in-out
                        ${open ? 'max-h-96 opacity-100 mt-1 mb-1' : 'max-h-0 opacity-0'}`}
          >
            {dropdownList.map((dropdownLink, index) => (
              <a
                key={index}
                role="menuitem"
                href={dropdownLink.linkURL}
                className="py-2 text-sm text-light-text
                           hover:text-light-main
                           focus:outline-none focus:ring-2 focus:ring-light-main/40
                           rounded duration-150"
              >
                {dropdownLink.linkText}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-row relative items-center"
      onKeyDown={handleKeyDown}
    >
      <a
        href={props.baseLinkURL.linkURL}
        className={headerTextClassNames}
        onMouseEnter={() => {
          clearCloseTimeout();
          setOpen(true);
        }}
        onMouseLeave={() => scheduleClose()}
        onFocus={() => {
          clearCloseTimeout();
          setOpen(true);
        }}
        onBlur={() => scheduleClose()}
        {...(hasDropdown
          ? {
              'aria-haspopup': 'menu',
              'aria-expanded': open,
              'aria-controls': submenuId
            }
          : {})}
      >
        {props.baseLinkURL.linkText}
      </a>
      {hasDropdown && (
        <div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={submenuId}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${
              props.baseLinkURL.linkText
            } submenu`}
            className="ml-1 p-1 rounded text-light-text hover:bg-light-border
                       focus:outline-none focus:ring-2 focus:ring-light-main/40
                       transition-colors duration-150"
          >
            <img
              src="/icons/chevron-down.svg"
              alt=""
              aria-hidden="true"
              className={`w-3 h-3 transition-transform duration-200 ${
                open ? 'rotate-180' : ''
              }`}
            />
          </button>
          <div
            id={submenuId}
            role="menu"
            onMouseEnter={() => {
              clearCloseTimeout();
              setOpen(true);
            }}
            onMouseLeave={() => scheduleClose()}
            onFocus={() => {
              clearCloseTimeout();
              setOpen(true);
            }}
            onBlur={() => scheduleClose()}
            className={`absolute left-1/2 -translate-x-1/2 top-full mt-1 bg-light-bg border text-light-text border-light-border shadow-lg transition-opacity duration-200 z-10 min-w-max ${
              open
                ? 'opacity-100 pointer-events-auto'
                : 'opacity-0 pointer-events-none'
            }`}
          >
            {dropdownList.map((dropdownLink, index) => {
              return (
                <a
                  key={index}
                  role="menuitem"
                  href={dropdownLink.linkURL}
                  className={dropdownItemClassNames}
                >
                  {dropdownLink.linkText}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default NavigationLink;
